import type { MediaItem } from "@/types/album";

/**
 * Contenu de démonstration de l'album.
 *
 * L'ordre du tableau est celui de la mosaïque : il a été composé à la main pour
 * l'équilibre visuel des colonnes et ne doit pas être trié chronologiquement.
 */

/**
 * Construit l'horodatage d'un souvenir de démonstration.
 *
 * Volontairement en heure **locale** : les dépôts réels des invités utilisent
 * `Date.now()`, donc un affichage uniforme en heure locale est la seule façon
 * de ne pas décaler les uns par rapport aux autres. L'heure de composition de
 * la maquette (« 17 h 08 ») reste ainsi affichée telle quelle sous tout fuseau.
 */
const at = (hours: number, minutes: number, dayOffset = 0): number =>
  new Date(2026, 5, 20 + dayOffset, hours, minutes).getTime();

const MOMENTS_SHEET = "/assets/wedding-moments-grid.webp";
const CEREMONY_PHOTO = "/assets/hero-wedding.webp";
const DANCEFLOOR_CLIP = "/assets/demo-dancefloor.mp4";

/** Découpes de la planche contact : elle contient quatre scènes en 2 × 2. */
const sheet = {
  cheers: { position: "0% 0%", size: "200% 200%" },
  friends: { position: "100% 0%", size: "200% 200%" },
  dinner: { position: "0% 100%", size: "200% 200%" },
  dance: { position: "100% 100%", size: "200% 200%" },
  dinnerAlt: { position: "0% 100%", size: "240% 240%" },
  danceAlt: { position: "100% 100%", size: "240% 240%" },
} as const;

export const DEMO_MEDIA: readonly MediaItem[] = Object.freeze([
  {
    id: "demo-camille-toast",
    kind: "image",
    origin: "demo",
    author: "Camille",
    moment: "cocktail",
    caption: "Le premier toast",
    createdAt: at(17, 8),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.cheers,
    span: "tall",
  },
  {
    id: "demo-lea-copines",
    kind: "image",
    origin: "demo",
    author: "Léa",
    moment: "cocktail",
    caption: "Les copines",
    createdAt: at(17, 21),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.friends,
  },
  {
    id: "demo-robin-oui",
    kind: "image",
    origin: "demo",
    author: "Robin",
    moment: "ceremonie",
    caption: "Juste après le grand oui",
    createdAt: at(16, 44),
    liked: false,
    previewUrl: CEREMONY_PHOTO,
    span: "hero",
  },
  {
    id: "demo-zoe-reunis",
    kind: "image",
    origin: "demo",
    author: "Zoé",
    moment: "diner",
    caption: "Tous réunis",
    createdAt: at(20, 16),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.dinner,
    span: "wide",
  },
  {
    id: "demo-antoine-piste",
    kind: "video",
    origin: "demo",
    author: "Antoine",
    moment: "soiree",
    caption: "La piste était à nous",
    createdAt: at(23, 47),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.dance,
    // La seule vidéo de la démonstration doit réellement se lire : un client
    // qui la clique et n'obtient qu'une image fixe conclut, à raison, que la
    // lecture vidéo n'est pas implémentée. Encodée en H.264 plutôt qu'en VP9,
    // dont la prise en charge par Safari reste incomplète.
    playbackUrl: DANCEFLOOR_CLIP,
    durationLabel: "0:06",
  },
  {
    id: "demo-marie-table",
    kind: "image",
    origin: "demo",
    author: "Marie",
    moment: "diner",
    caption: "À table !",
    createdAt: at(20, 19),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.dinnerAlt,
  },
  {
    id: "demo-nathan-danse",
    kind: "image",
    origin: "demo",
    author: "Nathan",
    moment: "soiree",
    caption: "Encore une danse",
    createdAt: at(0, 2, 1),
    liked: false,
    previewUrl: MOMENTS_SHEET,
    crop: sheet.danceAlt,
    span: "tall",
  },
  {
    id: "demo-emma-mains",
    kind: "image",
    origin: "demo",
    author: "Emma",
    moment: "ceremonie",
    caption: "Main dans la main",
    createdAt: at(16, 48),
    liked: false,
    previewUrl: CEREMONY_PHOTO,
    crop: { position: "35% 60%", size: "cover" },
  },
]);

/**
 * Souvenirs de la journée qui ne figurent pas dans la mosaïque de démonstration.
 * Le compteur de l'en-tête les additionne aux cartes réellement affichées pour
 * refléter la volumétrie d'un vrai mariage.
 */
export const DEMO_COUNT_OFFSET = 16;

/** Invités crédités dans la pile d'avatars de l'en-tête. */
export const DEMO_GUESTS = Object.freeze({
  initials: Object.freeze(["C", "L", "R"]),
  extra: 9,
});
